import { useState, useEffect, useCallback } from "react";
import { Plus, Building2, Edit2, Trash2, Globe, Phone, Mail } from "lucide-react";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";

var PRODUCT_LABELS = { auto: "Auto", residencial: "Resid.", vida: "Vida", empresarial: "Empr." };

function InsurerCard({ insurer, onEdit, onDelete, isAdmin }) {
  var products = insurer.products_offered || [];
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow" data-testid={"insurer-card-" + insurer.id}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#3B82F6]/10 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-[#3B82F6]" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-[#0A2F3D]">{insurer.name}</h4>
            {insurer.cnpj && <p className="text-xs text-gray-400">{insurer.cnpj}</p>}
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-1">
            <button onClick={function() { onEdit(insurer); }} className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-[#3B82F6]" data-testid={"edit-insurer-" + insurer.id}><Edit2 className="w-4 h-4" /></button>
            <button onClick={function() { onDelete(insurer.id); }} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500" data-testid={"delete-insurer-" + insurer.id}><Trash2 className="w-4 h-4" /></button>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-1 mb-3">
        {products.map(function(p) {
          var lbl = PRODUCT_LABELS[p] || p;
          return <span key={p} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-600">{lbl}</span>;
        })}
      </div>
      <div className="space-y-1 text-xs text-gray-500">
        {insurer.phone && <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" />{insurer.phone}</div>}
        {insurer.email && <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" />{insurer.email}</div>}
        {insurer.website && <div className="flex items-center gap-1.5"><Globe className="w-3 h-3" /><span className="truncate">{insurer.website}</span></div>}
      </div>
      <div className="mt-3 pt-2 border-t border-gray-50">
        <span className={"text-[10px] font-medium px-2 py-0.5 rounded-full " + (insurer.is_active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500")}>{insurer.is_active ? "Ativa" : "Inativa"}</span>
      </div>
    </div>
  );
}

export default function InsurersTab() {
  var auth = useAuth();
  var isAdmin = auth.user && auth.user.role === "admin";
  var insurersState = useState([]);
  var insurers = insurersState[0];
  var setInsurers = insurersState[1];
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  var showFormState = useState(false);
  var showForm = showFormState[0];
  var setShowForm = showFormState[1];
  var editingState = useState(null);
  var editing = editingState[0];
  var setEditing = editingState[1];

  var fetchInsurers = useCallback(function() {
    setLoading(true);
    api.get("/insurers").then(function(r) { setInsurers(r.data.insurers || []); }).finally(function() { setLoading(false); });
  }, []);

  useEffect(function() { fetchInsurers(); }, [fetchInsurers]);

  function handleSave() { setShowForm(false); setEditing(null); fetchInsurers(); }
  function handleEdit(ins) { setEditing(ins); setShowForm(true); }
  function handleDelete(id) {
    if (!window.confirm("Excluir esta seguradora?")) return;
    api.delete("/insurers/" + id).then(function() { fetchInsurers(); });
  }

  if (showForm) {
    return <InsurerForm insurer={editing} onSave={handleSave} onCancel={function() { setShowForm(false); setEditing(null); }} />;
  }

  return (
    <div data-testid="insurers-tab">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{insurers.length} seguradora(s) cadastrada(s)</p>
        {isAdmin && (
          <button onClick={function() { setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-lg text-sm font-medium hover:bg-blue-600" data-testid="new-insurer-btn">
            <Plus className="w-4 h-4" />Nova Seguradora
          </button>
        )}
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-[#3B82F6] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {insurers.map(function(ins) { return <InsurerCard key={ins.id} insurer={ins} onEdit={handleEdit} onDelete={handleDelete} isAdmin={isAdmin} />; })}
        </div>
      )}
    </div>
  );
}

function InsurerForm({ insurer, onSave, onCancel }) {
  var formState = useState({
    name: insurer ? insurer.name : "",
    cnpj: insurer ? (insurer.cnpj || "") : "",
    phone: insurer ? (insurer.phone || "") : "",
    email: insurer ? (insurer.email || "") : "",
    website: insurer ? (insurer.website || "") : "",
    products_offered: insurer ? (insurer.products_offered || []) : [],
    is_active: insurer ? insurer.is_active : true,
  });
  var form = formState[0];
  var setForm = formState[1];
  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];

  function handleChange(field, value) {
    setForm(function(prev) { var n = {}; for (var k in prev) n[k] = prev[k]; n[field] = value; return n; });
  }

  function toggleProduct(p) {
    var current = form.products_offered.slice();
    var idx = current.indexOf(p);
    if (idx >= 0) current.splice(idx, 1);
    else current.push(p);
    handleChange("products_offered", current);
  }

  function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    var payload = { name: form.name, cnpj: form.cnpj || null, phone: form.phone || null, email: form.email || null, website: form.website || null, products_offered: form.products_offered, is_active: form.is_active };
    if (insurer) {
      api.put("/insurers/" + insurer.id, payload).then(function() { onSave(); }).finally(function() { setSaving(false); });
    } else {
      api.post("/insurers", payload).then(function() { onSave(); }).finally(function() { setSaving(false); });
    }
  }

  var allProducts = ["auto", "residencial", "vida", "empresarial"];

  return (
    <div data-testid="insurer-form">
      <button onClick={onCancel} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">Voltar</button>
      <div className="bg-white rounded-xl p-6 shadow-sm border max-w-xl">
        <h3 className="text-lg font-bold text-[#0A2F3D] mb-4">{insurer ? "Editar Seguradora" : "Nova Seguradora"}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Nome</label>
            <input required value={form.name} onChange={function(e) { handleChange("name", e.target.value); }} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="insurer-name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">CNPJ</label>
              <input value={form.cnpj} onChange={function(e) { handleChange("cnpj", e.target.value); }} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="insurer-cnpj" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Telefone</label>
              <input value={form.phone} onChange={function(e) { handleChange("phone", e.target.value); }} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="insurer-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
              <input value={form.email} onChange={function(e) { handleChange("email", e.target.value); }} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="insurer-email" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Website</label>
              <input value={form.website} onChange={function(e) { handleChange("website", e.target.value); }} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="insurer-website" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Produtos Oferecidos</label>
            <div className="flex flex-wrap gap-2">
              {allProducts.map(function(p) {
                var active = form.products_offered.indexOf(p) >= 0;
                return (
                  <button key={p} type="button" onClick={function() { toggleProduct(p); }}
                    className={"px-3 py-1.5 rounded-lg text-xs font-medium border transition-all " + (active ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "bg-white text-gray-600 border-gray-200 hover:border-[#3B82F6]")}
                    data-testid={"product-toggle-" + p}
                  >{p.charAt(0).toUpperCase() + p.slice(1)}</button>
                );
              })}
            </div>
          </div>
          {insurer && (
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.is_active} onChange={function(e) { handleChange("is_active", e.target.checked); }} id="insurer-active" data-testid="insurer-active-toggle" />
              <label htmlFor="insurer-active" className="text-xs text-gray-600">Seguradora ativa</label>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
            <button type="submit" disabled={saving} className="px-4 py-2 bg-[#3B82F6] text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50" data-testid="save-insurer-btn">
              {saving ? "Salvando..." : (insurer ? "Salvar" : "Cadastrar")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
